<script setup lang="ts">
import { tryFormatDate } from '@tools/dates';
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import PakketpolisField from './PakketpolisField.vue';
import { usePakketpolisStore } from './pakketpolisStore';

const props = defineProps<{
  pakketpolisnummer: string;
}>();

const emit = defineEmits<{ edit: [] }>();

const store = usePakketpolisStore();
const pakketpolis = computed(() => store.getPakketpolis(props.pakketpolisnummer));
const isIncassoEditable = computed(
  () =>
    (pakketpolis.value?.mutatieActies.incasso.methode.isMogelijk ||
      pakketpolis.value?.mutatieActies.incasso.termijn.isMogelijk) ??
    false,
);
const incassoReden = computed(
  () =>
    pakketpolis.value?.mutatieActies.incasso.methode.melding ??
    pakketpolis.value?.mutatieActies.incasso.termijn.melding,
);
const incassomethode = computed(() => {
  const methode = pakketpolis.value?.mutatieActies.incasso.methode.opties.find(
    (o) => o.waarde === pakketpolis.value?.incassomethode,
  );
  // Als incassomethode niet gevonden kan worden in opteis, huidige methode teruggeven.
  // Dit om te voorkomen dat er undefined als mutatie komt te staan, bijvoorbeeld bij incassoco NT.
  if (methode === undefined) {
    return pakketpolis.value?.originelePakketpolis.incasso.omschrijving;
  }

  return methode?.label;
});
const incassotermijn = computed(() => {
  const value = pakketpolis.value?.incassotermijn.toLowerCase();

  return value !== 'halfjaar' ? value : 'half jaar';
});
</script>
<template>
  <div class="flex items-center justify-between">
    <div
      v-if="pakketpolis"
      class="grid px-4 py-6 lg:grid-cols-[auto_1fr] lg:gap-x-10 lg:gap-y-6 lg:px-8"
    >
      <PakketpolisField
        label="Ingangsdatum"
        :value="tryFormatDate(pakketpolis.originelePakketpolis.ingangsdatum)"
        :new-value="tryFormatDate(pakketpolis.ingangsdatum.toDateString())"
        :editable="pakketpolis.mutatieActies.ingangsdatum.isMogelijk"
        :reden="pakketpolis.mutatieActies.ingangsdatum.melding"
        @edit="emit('edit')"
        @remove="store.clearIngangsdatum(pakketpolisnummer)"
        class="max-lg:mb-5"
        data-testid="pakketpolis-details-ingangsdatum"
      />
      <PakketpolisField
        label="Hoofdvervaldatum"
        :value="tryFormatDate(pakketpolis.originelePakketpolis.hoofdvervaldatum)"
        :new-value="tryFormatDate(pakketpolis.hoofdvervaldatum.toDateString())"
        :editable="pakketpolis.mutatieActies.hoofdvervaldatum.isMogelijk"
        :reden="pakketpolis.mutatieActies.hoofdvervaldatum.melding"
        @edit="emit('edit')"
        @remove="store.clearHoofdvervaldatum(pakketpolisnummer)"
        class="max-lg:mb-5"
      />
      <PakketpolisField
        label="Incasso"
        :value="`${
          pakketpolis.originelePakketpolis.incasso.omschrijving
        } per ${pakketpolis.originelePakketpolis.termijn.omschrijving.toLowerCase()}`"
        :new-value="`${incassomethode} per ${incassotermijn}`"
        :editable="isIncassoEditable"
        :reden="incassoReden"
        @edit="emit('edit')"
        @remove="store.clearIncasso(pakketpolisnummer)"
      />
    </div>
    <div
      v-if="store.getPakketGeroyeerd(props.pakketpolisnummer)"
      class="flex items-center gap-2 px-4 lg:px-8"
    >
      <span class="max-w-20 truncate font-bold text-green sm:max-w-60">Royement pakket</span>
      <button @click="store.annuleerMutatie(props.pakketpolisnummer)" type="button">
        <FontAwesomeIcon icon="far fa-trash" class="cursor-pointer" />
      </button>
    </div>
  </div>
</template>
